export const POSTS = [
  {
    slug: "reclaim-20-hours-a-week-real-estate-agent",
    title: "How Real Estate Agents Can Reclaim 20+ Hours a Week",
    excerpt: "Most agents lose a full workday every week to tasks that aren't selling. Here's where those hours go, and how to get them back without hiring.",
    metaTitle: "How Real Estate Agents Can Reclaim 20+ Hours a Week",
    metaDescription: "Most agents lose a full workday every week to admin, follow-up and coordination. Here's exactly where the hours go and how to reclaim 20+ of them.",
    date: "2026-03-01",
    readTime: "6 min read",
    sections: [
      {
        h2: "Where Your Week Actually Goes",
        body: [
          "Ask an agent what they do and they'll say they sell homes. Track their week and you'll find something else: hours poured into follow-up, paperwork, scheduling, marketing, and chasing the other side of a deal. The selling, the part only you can do, often gets whatever is left over.",
          "The problem isn't effort. It's that the work around the deal expands to fill every open hour, and it quietly caps how much you can grow.",
        ],
      },
      {
        h2: "The Five Biggest Time Leaks",
        table: {
          head: ["Task", "Typical hrs/week", "Can be handed off?"],
          rows: [
            ["Admin & operations", "6-10", "Almost entirely"],
            ["Lead follow-up & calls", "4-8", "Mostly, with a human close"],
            ["Marketing & content", "3-6", "Yes, run on cadence"],
            ["Transaction coordination", "4-7", "Yes, end to end"],
            ["Scheduling & calendar", "2-4", "Yes"],
          ],
        },
        body: [
          "Add it up and the average producing agent is spending 20 to 30 hours a week on work that doesn't require their license, their relationships, or their judgment about a deal.",
        ],
      },
      {
        h2: "Why 'Just Hire Someone' Rarely Fixes It",
        body: [
          "The instinct is to hire an assistant. But hiring means recruiting, training, managing, and paying for someone whether the pipeline is busy or slow, and a general assistant still needs you to define every process. Most agents end up managing the helper instead of being freed by them.",
          "What actually creates leverage is a dedicated operator who already understands real estate and is amplified by automation, so the busywork runs itself and only real decisions reach your desk.",
        ],
      },
      {
        h2: "A Realistic Path to 20 Hours Back",
        body: [
          "Start by handing off the work that is pure process: transaction coordination, calendar, and the first-response layer of your leads. Then layer in marketing on a fixed cadence so your pipeline never goes quiet. Within a couple of weeks, the tasks that used to eat your evenings run in the background.",
          "The goal isn't to be less involved in your business. It's to spend your hours where the business actually grows, in front of clients, winning the next deal, instead of behind a screen keeping the last one alive.",
        ],
      },
      {
        h2: "What the Reclaimed Time Is Worth",
        body: [
          "Twenty hours a week is a thousand hours a year. Priced at even a conservative hourly value, that's tens of thousands of dollars of your time returned, before counting the deals you close simply because you finally have the capacity to chase them.",
          "That's the entire premise behind an AI-augmented right hand: give the selling back to the agent, and let everything else run quietly underneath.",
        ],
      },
    ],
  },
  {
    slug: "what-does-a-real-estate-transaction-coordinator-do",
    title: "What Does a Real Estate Transaction Coordinator Do?",
    excerpt: "A real estate transaction coordinator manages deadlines, disclosures, and closings so agents can focus on selling. Here's exactly what the role covers.",
    metaTitle: "What Does a Real Estate Transaction Coordinator Do?",
    metaDescription: "A real estate transaction coordinator manages deadlines, disclosures, and closings so agents can focus on selling. Here's exactly what the role covers.",
    date: "2026-01-15",
    readTime: "6 min read",
    sections: [
      {
        h2: "The Core Job: Owning a Deal From Contract to Close",
        body: [
          "A real estate transaction coordinator (TC) owns the operational life of a deal from the moment a contract is signed to the day your client gets the keys. Where an agent's job is to win the business and advise the client, the TC's job is to make sure the deal actually gets to the finish line, on time, in compliance, with nothing dropped.",
          "In practice that means being the single point of accountability for the file: opening it correctly, building the timeline, and driving every party toward closing. The agent stays focused on relationships and selling; the coordinator keeps the machinery running underneath.",
        ],
      },
      {
        h2: "Deadline & Contingency Tracking, Explained",
        body: [
          "Every contract is a stack of deadlines: inspection windows, financing dates, appraisal contingencies, contingency removals, and the closing date itself. Miss one and you don't just lose time, you can lose the deal or expose your client to real risk.",
          "A good TC pulls these dates off the contract the moment it's executed, builds them into a tracked timeline, and proactively flags each one before it becomes a problem. Nothing lives in the agent's head; everything lives in a system with alerts.",
        ],
      },
      {
        h2: "Disclosure and Document Management",
        body: [
          "Disclosures, addenda, and signatures are where deals quietly fall apart. A TC collects every required document, checks it for accuracy and completeness, and files it correctly the first time, so a missing initial doesn't surface the day before closing.",
          "This is also your compliance backbone. A clean, complete, audit-ready file protects both the agent and the brokerage long after the deal closes.",
        ],
      },
      {
        h2: "Coordinating Agents, Lenders, and Title",
        body: [
          "A closing involves a lot of people who don't naturally talk to each other: the buyer's and seller's agents, the lender, the title or escrow company, inspectors, and sometimes attorneys. The TC keeps everyone in sync, chasing status, confirming next steps, and making sure a file never stalls waiting on an update no one asked for.",
        ],
      },
      {
        h2: "In-House VA vs. Dedicated TC vs. AI-Augmented Partner",
        table: {
          head: ["", "In-house VA", "Dedicated TC", "AI-augmented partner"],
          rows: [
            ["Owns the deal to close", "Sometimes", "Yes", "Yes"],
            ["Proactive deadline alerts", "Rarely", "Manual", "Automated + flagged early"],
            ["Pulls dates off the contract", "No", "Manual", "Automatically"],
            ["Weekly pipeline reporting", "Ad hoc", "Usually", "Built in"],
            ["Scales with your volume", "Hard to hire", "One person", "Operator + automation"],
          ],
        },
        body: [
          "A plain virtual assistant processes what you send them. A dedicated TC owns the deal but is limited by human hours. An AI-augmented partner combines a trained coordinator with automation, pulling key dates, sending status updates the moment a file moves, and flagging missing documents before they threaten a closing date.",
        ],
      },
      {
        h2: "How Much Does a Real Estate Transaction Coordinator Cost?",
        body: [
          "Pricing ranges from per-file rates to monthly retainers depending on your volume and how much of the process you hand off. At Quexify, plans start at $750/month for part-time support (up to 20 hrs/week) and $1,500/month for a dedicated full-time operator, month to month, with a 30-day risk-free guarantee.",
          "For most active agents, the math is simple: if a coordinator saves even one deal a year from falling through, it pays for itself many times over.",
        ],
      },
    ],
  },
  {
    slug: "how-much-does-a-real-estate-transaction-coordinator-cost",
    title: "How Much Does a Real Estate Transaction Coordinator Cost?",
    excerpt: "Transaction coordinator pricing ranges from per-file fees to monthly retainers. Here's what you'll actually pay, and how to know it's worth it.",
    metaTitle: "How Much Does a Real Estate Transaction Coordinator Cost?",
    metaDescription: "Transaction coordinator pricing ranges from per-file fees to monthly retainers. Here's what you'll actually pay in 2026, and how to know it's worth it.",
    date: "2026-02-03",
    readTime: "7 min read",
    sections: [
      {
        h2: "The Three Ways Transaction Coordinators Charge",
        body: [
          "Most real estate transaction coordinators price one of three ways: per file, hourly, or on a monthly retainer. Per-file pricing typically runs $300 to $500 per closed transaction and suits agents with unpredictable volume. Hourly runs roughly $25 to $50 an hour but rarely covers full accountability. Monthly retainers buy dedicated capacity, which is where consistency and real ownership start to show up.",
        ],
      },
      {
        h2: "Per-File vs. Monthly: Which Is Cheaper?",
        body: [
          "The honest answer is: it depends on volume. If you close one or two deals a month, per-file keeps costs variable. Once you're consistently closing three or more, a monthly retainer usually costs less per transaction and buys you proactive coverage rather than reactive processing.",
        ],
      },
      {
        h2: "What Actually Drives the Price",
        table: {
          head: ["Factor", "Lower cost", "Higher cost"],
          rows: [
            ["Deal volume", "Steady, predictable", "Spiky, high-touch"],
            ["Scope", "Paperwork only", "Full contract-to-close"],
            ["Coordination", "You loop people in", "TC drives all parties"],
            ["Reporting", "On request", "Proactive weekly"],
          ],
        },
        body: [
          "The more of the process you hand off, the more you pay, and the more time you get back. The cheapest option on paper is rarely the cheapest once you factor in a deal that falls through from a missed deadline.",
        ],
      },
      {
        h2: "How Quexify Prices It",
        body: [
          "Quexify doesn't sell hours. We shape the engagement around your outcome: a per-deal model for solo agents, an embedded dedicated operator for growing teams, and outcome-based pricing for brokerages measured against faster closings and fewer fall-throughs. Every engagement is month to month.",
        ],
      },
      {
        h2: "So, Is It Worth It?",
        body: [
          "For most active agents the math is simple. If a coordinator saves even one deal a year from collapsing, and gives you back the hours you'd have spent chasing paperwork, it pays for itself many times over. The real question isn't cost, it's how many deals your current busywork is quietly costing you.",
        ],
      },
    ],
  },
];
